import type { ApiErrorBody } from '../types/api';
import type { TokenPair } from '../types/auth';
import { ApiError, AuthenticationRequiredError, NetworkError } from './errors';
import { MemoryTokenStore, type StoredTokens, type TokenStore } from './token-store';
import { queryString } from '../platform';

export interface HttpClientOptions {
  /** Origin the API lives on. Empty string means "same origin as this page". */
  baseUrl?: string;
  tokenStore?: TokenStore;
  fetchImpl?: typeof fetch;
  /** Called once the session is unrecoverable, so the app can route to sign-in. */
  onAuthenticationLost?: () => void;
  /** Called whenever a fresh token pair is obtained. */
  onTokensChanged?: (tokens: StoredTokens | null) => void;
  /** Retries for idempotent requests that fail on the network. */
  maxRetries?: number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Skip the access token entirely, for sign-in and public configuration. */
  anonymous?: boolean;
  /** Skip refresh-and-retry, so the refresh call itself cannot recurse. */
  noRefresh?: boolean;
  headers?: Record<string, string>;
}

const RETRY_BASE_MS = 300;
const RETRY_CAP_MS = 4000;
const REFRESH_MARGIN_MS = 30_000;
const IDEMPOTENT_METHODS = new Set(['GET', 'DELETE']);

function buildQuery(query: RequestOptions['query']): string {
  // Not `URLSearchParams`: React Native ships a subset of it, and keeping one
  // encoder means a URL built here is the same URL on both clients.
  return query ? queryString(query) : '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * The single place every HTTP call to the Liniya API goes through.
 *
 * It owns three concerns that would otherwise be re-implemented, and
 * re-implemented differently, at every call site: attaching the access token,
 * renewing it exactly once when several requests notice its expiry at the same
 * moment, and turning transport and protocol failures into two distinct error
 * types the interface can tell apart.
 */
export class LiniyaHttpClient {
  private readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly fetchImpl: typeof fetch;
  private readonly onAuthenticationLost: (() => void) | undefined;
  private readonly onTokensChanged: ((tokens: StoredTokens | null) => void) | undefined;
  private readonly maxRetries: number;
  /** In-flight refresh shared by every caller that needs a token right now. */
  private refreshInFlight: Promise<StoredTokens | null> | null = null;

  constructor(options: HttpClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.tokenStore = options.tokenStore ?? new MemoryTokenStore();
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onAuthenticationLost = options.onAuthenticationLost;
    this.onTokensChanged = options.onTokensChanged;
    this.maxRetries = options.maxRetries ?? 2;
  }

  get delivery() {
    return this.tokenStore.delivery;
  }

  async currentTokens(): Promise<StoredTokens | null> {
    return await this.tokenStore.read();
  }

  /** Store a pair the auth endpoints just returned. */
  async adoptTokenPair(pair: TokenPair): Promise<StoredTokens> {
    const tokens: StoredTokens = {
      accessToken: pair.access_token,
      accessExpiresAt: Date.now() + pair.expires_in * 1000,
      refreshToken: pair.refresh_token,
      deviceId: pair.device.id,
    };
    await this.tokenStore.write(tokens);
    this.onTokensChanged?.(tokens);
    return tokens;
  }

  async forgetTokens(): Promise<void> {
    await this.tokenStore.clear();
    this.onTokensChanged?.(null);
  }

  /**
   * Return a usable access token, renewing it first if it is about to expire.
   *
   * Concurrent callers share one refresh. Without that, a screen that fires
   * five queries on mount would send five refresh requests, and rotation would
   * make four of them look like a stolen-token replay and destroy the session.
   */
  private async accessToken(): Promise<string | null> {
    const stored = await this.tokenStore.read();
    if (stored && stored.accessExpiresAt - REFRESH_MARGIN_MS > Date.now()) {
      return stored.accessToken;
    }
    const refreshed = await this.refresh();
    return refreshed?.accessToken ?? null;
  }

  async refresh(): Promise<StoredTokens | null> {
    if (this.refreshInFlight) return await this.refreshInFlight;
    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return await this.refreshInFlight;
  }

  private async performRefresh(): Promise<StoredTokens | null> {
    const stored = await this.tokenStore.read();
    const body: Record<string, unknown> = { token_delivery: this.tokenStore.delivery };
    if (stored?.refreshToken) body.refresh_token = stored.refreshToken;
    try {
      const pair = await this.request<TokenPair>('/api/v1/auth/token/refresh/', {
        method: 'POST',
        body,
        anonymous: true,
        noRefresh: true,
      });
      return await this.adoptTokenPair(pair);
    } catch (error) {
      if (error instanceof NetworkError) throw error;
      await this.forgetTokens();
      this.onAuthenticationLost?.();
      return null;
    }
  }

  async request<TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> {
    const response = await this.raw(path, options);
    if (response.status === 204) return undefined as TResponse;
    const text = await response.text();
    if (!text) return undefined as TResponse;
    return JSON.parse(text) as TResponse;
  }

  /** Perform a request and return the raw response, for non-JSON endpoints. */
  async raw(path: string, options: RequestOptions = {}): Promise<Response> {
    const method = options.method ?? 'GET';
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    if (!options.anonymous) {
      const token = await this.accessToken();
      if (!token) throw new AuthenticationRequiredError();
      headers.Authorization = `Bearer ${token}`;
    }

    const url = `${this.baseUrl}${path}${buildQuery(options.query)}`;
    const init: RequestInit = {
      method,
      headers,
      // Sent so the browser attaches the refresh cookie on the auth endpoints.
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    };

    const response = await this.sendWithRetry(url, init, method, options.signal);

    if (response.status === 401 && !options.noRefresh && !options.anonymous) {
      // The token was rejected earlier than its stated expiry, which happens
      // when a device is revoked or a chain is signed out elsewhere.
      const refreshed = await this.refresh();
      if (!refreshed) throw new AuthenticationRequiredError();
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed.accessToken}` };
      const retried = await this.sendWithRetry(
        url,
        { ...init, headers: retryHeaders },
        method,
        options.signal,
      );
      if (!retried.ok) await this.throwApiError(retried);
      return retried;
    }

    if (!response.ok) await this.throwApiError(response);
    return response;
  }

  /**
   * Send a request, retrying transport failures on methods that are safe to
   * repeat. A dropped connection on a mobile network is normal; a lost `GET`
   * should not surface to the user as an error before it has been tried again.
   */
  private async sendWithRetry(
    url: string,
    init: RequestInit,
    method: string,
    signal: AbortSignal | undefined,
  ): Promise<Response> {
    let lastError: unknown;
    const attempts = IDEMPOTENT_METHODS.has(method) ? this.maxRetries + 1 : 1;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (signal?.aborted) throw new NetworkError('Запрос отменён.');
      try {
        return await this.fetchImpl(url, init);
      } catch (error) {
        lastError = error;
        if (signal?.aborted) throw new NetworkError('Запрос отменён.', error);
        if (attempt < attempts - 1) {
          await sleep(Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** attempt));
        }
      }
    }
    throw new NetworkError('Не удалось связаться с сервером.', lastError);
  }

  private async throwApiError(response: Response): Promise<never> {
    let body: ApiErrorBody = {
      code: `http_${response.status}`,
      message: 'Запрос не выполнен.',
      details: {},
      request_id: response.headers.get('X-Request-ID') ?? '',
    };
    try {
      const parsed = (await response.json()) as { error?: ApiErrorBody };
      if (parsed?.error) body = parsed.error;
    } catch {
      // A response without a JSON body keeps the synthesised error above.
    }
    throw new ApiError(response.status, body);
  }
}
